/**
 * The customer question: which accounts use this product, who at each, what
 * they use, and which way it is going. Admin-only detail (named people) is
 * gated by the API; this page just reflects what it is given.
 */
import { Link, useNavigate, useParams } from 'react-router';
import type { AccountHealth, AccountHealthRow, AccountPoint, AccountRow, FeatureAdoption, PersonRow } from '@totum-analytics/db';
import { ColumnChart } from '../charts/charts';
import { DataTable } from '../components/DataTable';
import { RangePicker, useRange } from '../components/RangePicker';
import { Badge, Card, Empty, ErrorNote, Loading } from '../components/ui';
import type { ApiError } from '../lib/api';
import { csvName } from '../lib/csv';
import { formatCount, formatDay, formatPercent, formatRelative } from '../lib/format';
import { rangeQuery } from '../lib/range';
import { useApi } from '../lib/useApi';
import { useSite } from './SiteLayout';

const UNATTRIBUTED = '(unattributed)';

export function accountName(a: { account_ref: string; display_name: string | null; is_unattributed: boolean }): string {
  if (a.is_unattributed) return 'Unattributed users';
  return a.display_name ?? a.account_ref;
}

const STATUS: Record<AccountHealthRow['status'], { tone: 'red' | 'yellow' | 'green' | 'blue'; icon: string; label: string }> = {
  dropping: { tone: 'red', icon: '▼', label: 'Dropping' },
  quiet: { tone: 'yellow', icon: '‖', label: 'Gone quiet' },
  healthy: { tone: 'green', icon: '✓', label: 'Healthy' },
  new: { tone: 'blue', icon: '★', label: 'New' },
};

export function HealthBadge({ status }: { status: AccountHealthRow['status'] }) {
  const s = STATUS[status];
  return <Badge tone={s.tone} icon={s.icon}>{s.label}</Badge>;
}

function UnattributedNote() {
  return (
    <p className="mt-4 text-xs text-gray-500 max-w-prose">
      <b className="font-medium text-gray-700">Unattributed users</b> are signed in, but the directory does not yet say
      which account they belong to. They are shown rather than dropped, so a missing person is a visible gap, not a quietly
      wrong total. The nightly directory sync fills them in.
    </p>
  );
}

export function AccountsList() {
  const { site } = useSite();
  const range = useRange();
  const navigate = useNavigate();
  const accounts = useApi<AccountRow[]>(`/sites/${site.id}/accounts?${rangeQuery(range)}`);
  const health = useApi<AccountHealth>(`/sites/${site.id}/accounts/health`);
  const statusOf = new Map(health.data?.accounts.map((a) => [a.account_ref, a.status]));

  return (
    <>
      <div className="mb-6"><RangePicker /></div>
      <Card title="Accounts using this product">
        {accounts.error && <ErrorNote error={accounts.error} onRetry={accounts.reload} />}
        {accounts.data ? (
          <>
            <DataTable
              rows={accounts.data}
              rowKey={(r) => r.account_ref}
              onRowClick={(r) => navigate(`/sites/${site.id}/accounts/${encodeURIComponent(r.account_ref)}?${rangeQuery(range)}`)}
              csvName={csvName(site.name, 'accounts', range.from, 'to', range.to)}
              empty={<Empty title="No signed-in usage in this period">Accounts appear once identified users have used the product.</Empty>}
              columns={[
                {
                  header: 'Account',
                  value: (r) => accountName(r),
                  render: (r) => (
                    <span className={r.is_unattributed ? 'italic text-gray-600' : 'font-medium text-gray-900'}>{accountName(r)}</span>
                  ),
                },
                {
                  header: 'Status',
                  value: (r) => statusOf.get(r.account_ref) ?? '',
                  render: (r) => {
                    // A bucket of unknown people has no trend worth judging.
                    const s = r.is_unattributed ? undefined : statusOf.get(r.account_ref);
                    return s ? <HealthBadge status={s} /> : '—';
                  },
                },
                { header: 'Active users', value: (r) => r.active_users, render: (r) => formatCount(r.active_users), align: 'right', bar: true },
                { header: 'Active days', value: (r) => r.active_days, render: (r) => formatCount(r.active_days), align: 'right' },
                { header: 'Sessions', value: (r) => r.sessions, render: (r) => formatCount(r.sessions), align: 'right' },
                { header: 'Pageviews', value: (r) => r.pageviews, render: (r) => formatCount(r.pageviews), align: 'right' },
                { header: 'Last seen', value: (r) => r.last_seen_at, render: (r) => formatRelative(r.last_seen_at), align: 'right' },
              ]}
            />
            {accounts.data.some((a) => a.is_unattributed) && <UnattributedNote />}
          </>
        ) : (
          !accounts.error && <Loading label="Loading accounts" />
        )}
      </Card>
    </>
  );
}

function change(c: number | null) {
  return c === null ? '—' : `${c > 0 ? '+' : c < 0 ? '−' : ''}${formatPercent(Math.abs(c))}`;
}

export function AccountHealthPage() {
  const { site } = useSite();
  const navigate = useNavigate();
  const { data, error, reload } = useApi<AccountHealth>(`/sites/${site.id}/accounts/health`);
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (!data) return <Loading label="Working out which accounts need attention" />;

  // The unattributed bucket is people we cannot place, not a customer: it gets no verdict.
  const judged = data.accounts.filter((a) => !a.is_unattributed);
  const attention = judged.filter((a) => a.status === 'dropping' || a.status === 'quiet');
  const t = data.thresholds;
  const columns = [
    { header: 'Account', value: (r: AccountHealthRow) => accountName(r), render: (r: AccountHealthRow) => <span className="font-medium text-gray-900">{accountName(r)}</span> },
    { header: 'Status', value: (r: AccountHealthRow) => STATUS[r.status].label, render: (r: AccountHealthRow) => <HealthBadge status={r.status} /> },
    { header: 'Users, last 7 days', value: (r: AccountHealthRow) => r.users_7d, render: (r: AccountHealthRow) => `${r.users_7d} (was ${r.users_prev_7d})`, align: 'right' as const },
    { header: 'Week on week', value: (r: AccountHealthRow) => r.change_7d, render: (r: AccountHealthRow) => change(r.change_7d), align: 'right' as const },
    { header: 'Users, last 28 days', value: (r: AccountHealthRow) => r.users_28d, render: (r: AccountHealthRow) => `${r.users_28d} (was ${r.users_prev_28d})`, align: 'right' as const },
    { header: 'Month on month', value: (r: AccountHealthRow) => r.change_28d, render: (r: AccountHealthRow) => change(r.change_28d), align: 'right' as const },
    { header: 'Last active', value: (r: AccountHealthRow) => r.last_seen, render: (r: AccountHealthRow) => formatDay(r.last_seen, true), align: 'right' as const },
  ];
  const open = (r: AccountHealthRow) => navigate(`/sites/${site.id}/accounts/${encodeURIComponent(r.account_ref)}`);

  return (
    <>
      <Card title="Needs attention" className="mb-6">
        <p className="text-sm text-gray-600 mb-4 max-w-prose">
          <b className="font-medium text-gray-900">Dropping</b> means active users fell by {formatPercent(t.drop_threshold)} or more against
          the previous week or the previous 28 days, from at least {t.min_baseline} users (below that, a percentage is noise).{' '}
          <b className="font-medium text-gray-900">Gone quiet</b> means no activity for {t.quiet_after_days} days. Accounts with less than two
          weeks of history are marked new rather than judged. As of {formatDay(data.as_of, true)}.
        </p>
        <DataTable
          rows={attention}
          rowKey={(r) => r.account_ref}
          onRowClick={open}
          csvName={csvName(site.name, 'needs-attention', data.as_of)}
          empty={<Empty title="Nothing needs attention">Every account with enough history is holding steady.</Empty>}
          columns={columns}
        />
      </Card>
      <Card title="Every account">
        <DataTable rows={judged} rowKey={(r) => r.account_ref} onRowClick={open} columns={columns} csvName={csvName(site.name, 'account-health', data.as_of)} />
      </Card>
    </>
  );
}

/** Sequential shade for a share of an account's users: five steps of one hue. */
function shade(share: number): { bg: string; ink: string } {
  const steps = ['var(--color-seq-1)', 'var(--color-seq-2)', 'var(--color-seq-3)', 'var(--color-seq-4)', 'var(--color-seq-5)'];
  const i = Math.min(4, Math.floor(share * 5));
  return { bg: steps[i] as string, ink: i >= 2 ? '#ffffff' : '#0b2540' };
}

export function AdoptionPage() {
  const { site } = useSite();
  const range = useRange();
  const { data, error, reload } = useApi<FeatureAdoption>(`/sites/${site.id}/accounts/adoption?${rangeQuery(range)}`);

  return (
    <>
      <div className="mb-6"><RangePicker /></div>
      <Card title="Feature adoption">
        {error && <ErrorNote error={error} onRetry={reload} />}
        {!data && !error && <Loading label="Loading adoption" />}
        {data && !data.instrumented && (
          <Empty title="This site is not instrumented for features yet">
            Adoption is built from custom events, and this site has never sent one. That means "not measured", not "not used".
            Add a <code className="bg-gray-100 px-1 rounded">track</code> call where each feature is used; see the Install tab.
          </Empty>
        )}
        {data && data.instrumented && data.features.length === 0 && (
          <Empty title="No feature use in this period">The site is instrumented, but no custom events arrived in these dates.</Empty>
        )}
        {data && data.instrumented && data.features.length > 0 && (
          <>
            <p className="text-sm text-gray-600 mb-4 max-w-prose">
              Each cell is the share of an account's active users who used the feature in this period. A dash means nobody did:
              this site is instrumented, so that is a real "not used".
            </p>
            <div className="overflow-x-auto -mx-2">
              <table className="text-sm min-w-full">
                <thead>
                  <tr>
                    <th scope="col" className="px-2 py-2 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">Account</th>
                    {data.features.map((f) => (
                      <th key={f} scope="col" className="px-2 py-2 text-center text-xs text-gray-500 font-medium font-mono">{f}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((a) => (
                    <tr key={a.account_ref} className="border-t border-gray-100">
                      <th scope="row" className={`px-2 py-2 text-left font-medium whitespace-nowrap ${a.is_unattributed ? 'italic text-gray-600' : 'text-gray-900'}`}>
                        {accountName(a)} <span className="text-gray-500 font-normal tabular">· {a.active_users}</span>
                      </th>
                      {data.features.map((f) => {
                        const cell = data.cells.find((c) => c.account_ref === a.account_ref && c.feature === f);
                        if (!cell) return <td key={f} className="px-1 py-1 text-center text-gray-400" title="Not used">—</td>;
                        const share = a.active_users > 0 ? cell.users / a.active_users : 0;
                        const { bg, ink } = shade(share);
                        return (
                          <td key={f} className="px-1 py-1">
                            <div
                              className="rounded-md px-2 py-1.5 text-center tabular text-xs font-medium"
                              style={{ background: bg, color: ink }}
                              title={`${cell.users} of ${a.active_users} users, ${cell.events} uses`}
                            >
                              {cell.users}/{a.active_users}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-600" aria-hidden="true">
              <span>Fewer users</span>
              {[0.1, 0.3, 0.5, 0.7, 0.9].map((s) => (
                <span key={s} className="inline-block w-6 h-3 rounded-sm" style={{ background: shade(s).bg }} />
              ))}
              <span>All users</span>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

export function AccountDetail() {
  const { site } = useSite();
  const { ref = '' } = useParams();
  const range = useRange();
  const q = rangeQuery(range);
  const base = `/sites/${site.id}/accounts/${encodeURIComponent(ref)}`;
  const series = useApi<AccountPoint[]>(`${base}/timeseries?${q}`);
  const people = useApi<PersonRow[]>(`${base}/people?${q}`);
  const accounts = useApi<AccountRow[]>(`/sites/${site.id}/accounts?${q}`);
  const health = useApi<AccountHealth>(`/sites/${site.id}/accounts/health`);
  const account = accounts.data?.find((a) => a.account_ref === ref);
  const status = ref === UNATTRIBUTED ? undefined : health.data?.accounts.find((a) => a.account_ref === ref)?.status;
  const name = account ? accountName(account) : ref === UNATTRIBUTED ? 'Unattributed users' : ref;
  const forbidden = (people.error as ApiError | undefined)?.status === 403;

  return (
    <>
      <nav className="text-sm mb-3">
        <Link to={`/sites/${site.id}/accounts?${q}`} className="text-totum-ink hover:underline">← All accounts</Link>
      </nav>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">{name}</h2>
          {status && <HealthBadge status={status} />}
        </div>
        <RangePicker />
      </div>

      <Card title="Active users per day" className="mb-6">
        {series.error && <ErrorNote error={series.error} onRetry={series.reload} />}
        {series.data ? (
          <ColumnChart
            label={`Active users per day for ${name}`}
            name="Active users"
            color="var(--color-series-1)"
            days={series.data.map((d) => d.day)}
            values={series.data.map((d) => d.active_users)}
          />
        ) : (
          <Loading />
        )}
      </Card>

      <Card title="People">
        {forbidden ? (
          <p className="text-sm text-gray-600">Your role can see this account's totals, but not the named individuals in it.</p>
        ) : people.error ? (
          <ErrorNote error={people.error} onRetry={people.reload} />
        ) : people.data ? (
          <DataTable
            rows={people.data}
            rowKey={(r) => r.user_ref}
            csvName={csvName(site.name, name, 'people', range.from, 'to', range.to)}
            columns={[
              {
                header: 'Person',
                value: (r) => r.display_name ?? r.user_ref,
                render: (r) => (
                  <span>
                    <span className="text-gray-900 font-medium">{r.display_name ?? <span className="font-mono text-xs">{r.user_ref}</span>}</span>
                    {r.is_active === false && <span className="ml-2"><Badge>Left</Badge></span>}
                    {r.email && <span className="block text-xs text-gray-500">{r.email}</span>}
                  </span>
                ),
              },
              { header: 'Active days', value: (r) => r.active_days, render: (r) => formatCount(r.active_days), align: 'right', bar: true },
              { header: 'Sessions', value: (r) => r.sessions, render: (r) => formatCount(r.sessions), align: 'right' },
              { header: 'Pageviews', value: (r) => r.pageviews, render: (r) => formatCount(r.pageviews), align: 'right' },
              {
                header: 'Features used',
                value: (r) => r.features.join(' '),
                render: (r) => (r.features.length ? <span className="font-mono text-xs text-gray-700">{r.features.join(', ')}</span> : <span className="text-gray-400">—</span>),
              },
              { header: 'Last seen', value: (r) => r.last_seen_at, render: (r) => formatRelative(r.last_seen_at), align: 'right' },
            ]}
          />
        ) : (
          <Loading label="Loading people" />
        )}
        {ref === UNATTRIBUTED && <UnattributedNote />}
      </Card>
    </>
  );
}
